/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState, useRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
} from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import TablePagination from "@mui/material/TablePagination";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
}) {
  const tableContainerRef = useRef(null);
  const defaultValue = entriesPerPage.defaultValue
    ? entriesPerPage.defaultValue
    : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  // Store current pagination state in a ref to persist during scrolling
  const paginationStateRef = useRef({
    pageIndex: 0,
    pageSize: defaultValue || 10
  });

  const tableInstance = useTable(
    { 
      columns, 
      data, 
      initialState: { 
        pageIndex: paginationStateRef.current.pageIndex,
        pageSize: paginationStateRef.current.pageSize 
      },
      // Use manual pagination to prevent automatic resets
      manualPagination: false,
      // Don't let the table reset page index when data changes
      autoResetPage: false
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    gotoPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set initial page size once
  useEffect(() => {
    setPageSize(paginationStateRef.current.pageSize);
  }, []);

  // Update the ref whenever pageIndex or pageSize changes
  useEffect(() => {
    paginationStateRef.current.pageIndex = pageIndex;
    paginationStateRef.current.pageSize = pageSize;
  }, [pageIndex, pageSize]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0
      ? gotoPage(0)
      : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) =>
    gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  const entriesStart = pageIndex === 0 ? 1 : pageIndex * pageSize + 1;
  const entriesEnd = Math.min(entriesStart + pageSize - 1, rows.length);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  return (
    <TableContainer
      ref={tableContainerRef}
      sx={{
        boxShadow: "none",
        height: "722px",
        overflowY: "auto",
        "&::-webkit-scrollbar-thumb": {
          background: "#gray",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar": {
          height: '9px', 
        }
      }}
    >
      <Table {...getTableProps()} stickyHeader>
        <MDBox component="thead" sx={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#fff" }}>
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => (
                <DataTableHeadCell
                  key={idx}
                  {...column.getHeaderProps(
                    isSorted && column.getSortByToggleProps()
                  )}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
          {/* Single Bottom Border for the Header Row */}
          <MDBox
            component="div"
            sx={{
              position: "absolute",
              bottom: 0,
              left: "10px",
              width: "98%",
              height: "1px",
              backgroundColor: "#CDCDCD",
              margin: "0 auto",
            }}
          />
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow
                key={key}
                {...row.getRowProps()}
                sx={{
                  width: "98%",
                  marginLeft: "auto",
                  marginRight: "auto",
                  position: "relative",
                }}
              >
                {row.cells.map((cell, idx) => (
                  <DataTableBodyCell
                    key={idx}
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}

                    {/* Applying the bottom border for the entire row */}
                    <MDBox
                      component="div"
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: "50%",
                        width: "98%",
                        height: "1px",
                        backgroundColor: "#CDCDCD",
                        transform: "translateX(-50%)",
                      }}
                    />
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Pagination After table rendering code */}
      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        sx={{ position: "sticky", bottom: 0, backgroundColor: "#fff", zIndex: 10 }}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography
              variant="button"
              color="secondary"
              fontWeight="regular"
            >
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )}

        <MDBox
          display="flex"
          alignItems="center"
          sx={{
            position: "absolute",
            left: 10,
            bottom: 10,
            top: 30,
          }}
          style={{marginTop: "12px"}}
        >
          <MDTypography variant="body2">Rows per page</MDTypography>
          <Select
            value={pageSize}
            onChange={(event) => {
              setPageSize(parseInt(event.target.value, 10));
              gotoPage(0);
            }}
            sx={{
              width: 75,
              height: 40,
              marginRight: 1,
              marginLeft: 1,
              "& .MuiSelect-select": {
                fontSize: "14px",
                color: "#262626",
              },
            }}
          >
            <MenuItem value={5}>5/page</MenuItem>
            <MenuItem value={10}>10/page</MenuItem>
          </Select>

          <MDTypography variant="body2" color="black">
            Go to
          </MDTypography>
          <TextField
            type="number"
            variant="outlined"
            size="small"
            value={pageIndex + 1}
            onChange={(event) => {
              const value = Math.max(
                1,
                Math.min(
                  Number(event.target.value),
                  Math.ceil(rows.length / pageSize)
                )
              );
              gotoPage(value - 1);
            }}
            sx={{ width: 60, marginRight: 1, marginLeft: 1,marginTop: 1 }}
          />
        </MDBox>

        {pageOptions.length > 1 && (
          <MDBox
            sx={{
              position: "absolute",
              bottom: 10,
              right: 10,
              marginTop: "16px",
              display: { xs: 'flex', sm: 'flex' },
            }}
          >
            <Pagination
              count={Math.ceil(rows.length / pageSize)}
              page={pageIndex + 1}
              onChange={(event, value) => gotoPage(value - 1)}
              variant="outlined"
              shape="rounded"
              sx={{ mt: { xs: 2, sm: 0 }, ml: { xs: 0, sm: 2 } }}
            />
          </MDBox>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;